import { ReactElement } from "react"
import { useTranslation } from "react-i18next"
import { ErrorResponse, useRouteError } from "react-router"
import { faro } from "@grafana/faro-react"
import { AppInsights } from "@/app-insights.ts"
import { useReleaseChangePolling } from "@/hooks/useReleaseChangePolling"
import { ReportIssue } from "@/modules/common/report-issue/report-an-issue"
import { StaleVersionNotice } from "@/modules/common/report-issue/stale-version-notice"
import { SofiaEnvironment } from "@/sofia-environment"
import { useVersionStore } from "@/stores/useVersionStore"

const RootErrorBoundary = (props: { children?: ReactElement }) => {
  const routeError = useRouteError()
  const error = routeError as ErrorResponse
  const { t } = useTranslation()
  useReleaseChangePolling()
  const isStaleVersion = useVersionStore((state) => state.isStaleVersion)

  const status = error.status || 500
  const errorTranslationKey = status === 401 ? "unauthorized" : status

  // ErrorReponse isn't properly serializable (because properties aren't enumerable), so we create a new object with a copy of all its properties
  const serializableError = Object.fromEntries(
    // @ts-expect-error TS7053
    Object.getOwnPropertyNames(error).map((prop) => [prop, error[prop]]),
  )

  if (!SofiaEnvironment.IsWindowClosing) {
    AppInsights.trackEvent({
      name: "full_screen_error",
      properties: { error: serializableError, displayStatus: status },
    })

    if (faro && faro.api && !faro.config.paused) {
      faro.api.pushError(new Error("Full Screen Error"), {
        type: "full_screen_error",
        context: {
          error: JSON.stringify(serializableError),
          displayStatus: status.toString(),
        },
      })
    }
  }

  return (
    <div className="h-screen w-screen overflow-hidden">
      <main className="bg-background-subdued flex h-full flex-col items-center justify-center">
        <h1 className="text-on-surface text-xl font-bold">{t(`error.${errorTranslationKey}.title`)}</h1>
        <div className="mb-4">{t(`error.${errorTranslationKey}.description`)}</div>
        {props.children}
        <div className="w-fit p-8">{isStaleVersion ? <StaleVersionNotice open /> : <ReportIssue open />}</div>
      </main>
    </div>
  )
}
RootErrorBoundary.displayName = "RootErrorBoundary"

export { RootErrorBoundary }
